import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Wallbox } from '../../shared';
import { useSelector } from '../../store/utils';

import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';

import { WallboxTypeSelect } from './wallbox-type-select';
import { trpc } from '../../api/trpc';
type Props = {
  open: boolean;
  onClose: () => void;
};

export const WallboxWizard = ({ open, onClose }: Props) => {
  const wallboxTypeFromStore = useSelector((s) => s.variables.WB_TYPE);
  const wallboxConfigFromStore = useSelector(
    (s) => s.variables.WALLBOX_CONFIG || {}
  );

  const [wallboxType, setWallboxType] = useState<Wallbox>(Wallbox.Solax);
  const [multipleWallboxes, setMultipleWallboxes] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [submitErrorDetails, setSubmitErrorDetails] = useState(false);
  const addressTypeVisible =
    wallboxType === Wallbox.VictronEvCharger ||
    wallboxType === Wallbox.Alfen ||
    wallboxType === Wallbox.EvMate ||
    wallboxType === Wallbox.MyBox ||
    wallboxType === Wallbox.GoodweEvCharger ||
    wallboxType === Wallbox.Ecovolter;
  const defaultWallboxIpAddress = '100.100.1.120';
  const [wallboxIpAddress, setWallboxIpAddress] = useState(
    defaultWallboxIpAddress
  );

  // Initialize form based on current config
  useEffect(() => {
    if (open) {
      // If WB_TYPE is Next and there are wallboxes in config, use first one's type
      if (
        wallboxTypeFromStore === Wallbox.Next &&
        Object.keys(wallboxConfigFromStore).length > 0
      ) {
        const firstWallbox = Object.values(wallboxConfigFromStore)[0];
        if (firstWallbox) {
          setWallboxType(firstWallbox.type);
          if ('address' in firstWallbox && firstWallbox.address) {
            setWallboxIpAddress(firstWallbox.address);
          }
        }
      } else if (wallboxTypeFromStore !== Wallbox.None) {
        setWallboxType(wallboxTypeFromStore);
      }
    }
  }, [open, wallboxTypeFromStore, wallboxConfigFromStore]);

  const { mutate, isLoading } = trpc.wallbox.wizardSubmit.useMutation({
    onMutate() {
      setSubmitError(undefined);
      setSubmitErrorDetails(false);
    },
    onSuccess() {
      onClose();
    },
    onError(error) {
      setSubmitError(error.message);
    },
  });

  const submitDisabled = isLoading || multipleWallboxes;

  const handleSubmit = () => {
    if (multipleWallboxes) {
      return;
    }

    // Validate IP address if required
    if (addressTypeVisible && !wallboxIpAddress) {
      setSubmitError('IP adresa je povinná');
      return;
    }

    mutate({
      wallboxType,
      wallboxIpAddress: addressTypeVisible ? wallboxIpAddress : undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <FormattedMessage
          id="wallbox_wizard.title"
          defaultMessage="Nastavení wallbox"
        />
      </DialogTitle>
      <DialogContent>
        <Stack gap={2.5} sx={{ py: 2 }}>
          {submitError && (
            <Alert severity="error">
              {submitError}
              {submitErrorDetails && (
                <pre style={{ fontSize: '0.8em', marginTop: 8 }}>
                  {JSON.stringify(submitError, null, 2)}
                </pre>
              )}
            </Alert>
          )}

          <WallboxTypeSelect
            value={wallboxType}
            onChange={(type) => {
              setWallboxType(type);
              // Reset IP address when changing type
              if (
                type !== Wallbox.VictronEvCharger &&
                type !== Wallbox.Alfen &&
                type !== Wallbox.EvMate &&
                type !== Wallbox.MyBox &&
                type !== Wallbox.GoodweEvCharger &&
                type !== Wallbox.Ecovolter
              ) {
                setWallboxIpAddress(defaultWallboxIpAddress);
              }
            }}
          />
          {addressTypeVisible && (
            <>
              <TextField
                label={
                  <FormattedMessage
                    id="wallbox_wizard.custom_device_ip_address.input"
                    defaultMessage="IP adresa wallboxu"
                  />
                }
                value={wallboxIpAddress}
                onChange={(e) => setWallboxIpAddress(e.target.value)}
                fullWidth
                required
                error={addressTypeVisible && !wallboxIpAddress}
              />
              <Alert icon={false} color="warning">
                <FormattedMessage
                  id="wallbox_wizard.custom_device_ip_address.alert"
                  defaultMessage="Ujistěte se prosím, že se IP adresa wallboxu nebude měnit (např. když wallbox využívá adresu z DHCP)."
                />
              </Alert>
            </>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={multipleWallboxes}
                onChange={(e) => setMultipleWallboxes(e.target.checked)}
              />
            }
            label={
              <FormattedMessage
                id="wallbox_wizard.multiple_wallboxes.label"
                defaultMessage="Více wallboxů"
              />
            }
            sx={{ mt: -1 }}
          />

          {multipleWallboxes && (
            <>
              <Alert icon={false} color="warning">
                <FormattedMessage
                  id="wallbox_wizard.multiple_wallboxes.alert"
                  defaultMessage="Pro nastavení více wallboxů prosím kontaktujte linku podpory. Kontakt najdete v technické wiki Infigy."
                />
              </Alert>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <FormattedMessage
            id="wallbox_wizard.cancel"
            defaultMessage="Zrušit"
          />
        </Button>
        <Button
          variant="contained"
          disabled={submitDisabled}
          onClick={handleSubmit}
        >
          <FormattedMessage id="wallbox_wizard.save" defaultMessage="Uložit" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
